import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import {
    WB_GeneralLedgerAccountRow, WB_MetricDefinitionEventSpendRow,
    WB_MetricDefinitionGLAccountRow,
    WB_MetricDefinitionRow, WB_TPM_EventSpendTypeRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbMetricDefinition } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_CubeSource,
    esWB_CubeSource_DataSource, esWB_DataObject,
    esWB_Event_Spend_Type,
    esWB_General_Ledger_Account,
    esWB_Metric_Definition, esWB_MetricDefinition_Component, esWB_MetricDefinition_EventSpend_Type,
    esWB_MetricDefinition_GLAccount
} from "../PA.Core/PAdbSchema";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { MetricDefinitionBOValidations } from "./MetricDefinitionBO.Validations";
import { PAEntity } from "../PA.Core/PAEntity";

import { IPolicyCollection } from "../PA.Core/PA.Core.IF.Policy";
import { IHaveSecurity } from "../PA.Core/PA.Core.IF.Aspects";
import { PolicyCollection } from "../PA.Core/Security/PolicyCollection";
import { PolicyBOis } from "../PA.Core/Security/PolicyBOis";
import { PolicyHasRole } from "../PA.Core/Security/PolicyHasRole";
import { MetricDefinitionPolicy } from "../PA.MD.BusinessObjects/MetricDefinitionPolicy";
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { RecordUtils } from "../PA.Core/Basic/Records";
import { AuditService } from "../PA.Core/Security/AuditService";
import { BaseLnBOAudit } from "./BaseLnBO.Audit";
import { MetricDefinitionBOAudit } from "./MetricDefinitionBO.Audit";

export interface IMetricDefinitionLoadOptions {
    dbContext?: PAdbContext;
}

const MetricDefinitionLoadOptions_default: IMetricDefinitionLoadOptions = {};

export class MetricDefinitionBO
    implements IBusinessObject,
        IHaveSecurity {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_MetricDefinition: WB_MetricDefinitionRow;


    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New MetricDefinition" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_MetricDefinition = undefined;

        this.IHaveSecurity_init();
    }

    //</editor-fold>

    get WB_MetricDefinition(): WB_MetricDefinitionRow {
        return this.drWB_MetricDefinition;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating MetricDefinition");

        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new MetricDefinition.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Metric_Definition)
                .then((newMetricDefinitionRow: WB_MetricDefinitionRow) => {
                    newMetricDefinitionRow.setDefaults();
                    newMetricDefinitionRow.cif.MANUAL_IND = "1";
                    self.drWB_MetricDefinition = newMetricDefinitionRow;
                    self.dbContext.add(esWB_Metric_Definition, newMetricDefinitionRow);
                    self.Identity.setAll(self.WB_MetricDefinition.cif.WB_MTRDFN_ID, self.WB_MetricDefinition.cif.METRIC_ID, self.WB_MetricDefinition.cif.METRIC_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_MetricDefinition, "WB_MTRDFN_ID", "METRIC_ID", "METRIC_NAME");

                    return self.IHaveSecurity_OnLoadComplete();
                })
                .then((ignore) => {

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                });
        });
        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");
        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {

            if (self.bIsLoaded) {
                let sError = "MetricDefinition already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(self); // "MetricDefinition already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, MetricDefinitionLoadOptions_default);

            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null) {
                self.dbContext = new PAdbContext({});
            }

            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext for MetricDefinition.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = MetricDefinitionBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = MetricDefinitionBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function() {
                    let aMetricDefinitionRow: any = recordResolver.get(esWB_Metric_Definition, anId);

                    if (_.isEmpty(aMetricDefinitionRow)) {
                        self._OnLoadComplete = null;
                        reject("MetricDefinition not Found");
                        resolve(self);
                        return;
                    }

                    self.drWB_MetricDefinition = aMetricDefinitionRow;
                    self.Identity.setAll(self.WB_MetricDefinition.cif.WB_MTRDFN_ID, self.WB_MetricDefinition.cif.METRIC_ID, self.WB_MetricDefinition.cif.METRIC_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_MetricDefinition, "WB_MTRDFN_ID", "METRIC_ID", "METRIC_NAME");

                    return self.IHaveSecurity_OnLoadComplete();
                })
                .then((ignore) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function(err: any) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        if (this._bIsLoaded) {
            return ASync.resolve(this);
        }

        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_MetricDefinition.cif.METRIC_NAME + "(" + self.WB_MetricDefinition.cif.METRIC_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext for to Validate MetricDefinition.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            MetricDefinitionBOValidations.ValidateFields(self)
                .then((ignore) => {
                    self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        let aPAEntity = new PAEntity();
        const wbMetricDefinition = <IwbMetricDefinition>self.drWB_MetricDefinition;
        self.SaveProgressProvider.start("Saving " + wbMetricDefinition.METRIC_NAME + "(" + wbMetricDefinition.METRIC_ID + ")");
        let auditService: AuditService = undefined;

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save MetricDefinition.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let bDone = false;
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }
                }).then((ignore) => {
                if (bDone) return;
                return MetricDefinitionBOAudit.MetricDefinitionAuditBO(self);
            }).then((anAuditService) => {
                if (bDone) return;
                auditService = anAuditService;
                return saveFunction(esWB_Metric_Definition, self.drWB_MetricDefinition, {});
            })
                .then((ignored) => {
                    if (bDone) return;
                    if (auditService) {
                        return auditService.Save(bBatch);
                    }
                }).then((ignore) => {
                if (bDone) return;
                let tasks: Promise<any> [] = [];
                tasks.push(saveFunction(esWB_MetricDefinition_GLAccount, self.drWB_MetricDefinition.WB_MetricDefinitionGLAccountRows));
                tasks.push(saveFunction(esWB_MetricDefinition_EventSpend_Type, self.drWB_MetricDefinition.WB_MetricDefinitionEventSpendRows));
                return Promise.all(tasks);
            })
                .then((ignored) => {
                    if (bDone) return;
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                            if (self.drWB_MetricDefinition.cif.METRIC_TYPE === "foundation")
                                return aPAEntity.syncSubjectArea(self.drWB_MetricDefinition.cif.WB_SUBJECTAREA_ID);
                        });
                    } else {
                        self._bIsSaved = true;
                        if (self.drWB_MetricDefinition.cif.METRIC_TYPE === "foundation")
                            return aPAEntity.syncSubjectArea(self.drWB_MetricDefinition.cif.WB_SUBJECTAREA_ID);
                    }
                }).then(() => {
                let dctLeaseClasses: { [id: string]: string } = {};
                dctLeaseClasses[LeaseRegistry.Lease_Type_Metrics] = LeaseRegistry.Lease_Type_Metrics;
                dctLeaseClasses[LeaseRegistry.Lease_Type_SubjectArea] = LeaseRegistry.Lease_Type_SubjectArea;
                if (!_.isEmpty(_.values(dctLeaseClasses))) {
                    LeaseRegistry.generateLeaseInvalidationByClassList(_.values(dctLeaseClasses));
                }
                return self.IHaveSecurity_AfterSaveComplete();
            }).then(() => {
                self.SaveProgressProvider.finish();
                resolve(self);
            }).catch((err) => {
                self.SaveProgressProvider.exception(err);
                resolve(self);
            });
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbMetricDefinition = <IwbMetricDefinition>self.drWB_MetricDefinition;
        let aPAEntity = new PAEntity();
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbMetricDefinition.METRIC_NAME + "(" + wbMetricDefinition.WB_METRIC_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            // 1. Validate if we can delete the MetricDefinition

            // 2. If Valid, mark all the records for deletion.
            self.drWB_MetricDefinition.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        return aPAEntity.syncSubjectArea(self.drWB_MetricDefinition.cif.WB_SUBJECTAREA_ID);
                    }).then(() => {
                    self.drWB_MetricDefinition.forceDelete();
                    resolve(self);
                })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        reject(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this;

        const src: MetricDefinitionBO = this;

        const tgt = new MetricDefinitionBO({});
        await tgt.LoadNew();

        if (!tgt.bIsLoaded)
            throw new Error("Unable to create New Metric");

        RecordUtils.CopyProperties(tgt.dbContext, esWB_Metric_Definition, src.WB_MetricDefinition, tgt.WB_MetricDefinition,
            [],
            ["METRIC_ID", "METRIC_NAME", "INTEGRATION_IND", "CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
            {});
        tgt.WB_MetricDefinition.cif.METRIC_ID = "CopyOf" + src.WB_MetricDefinition.cif.METRIC_ID;
        tgt.WB_MetricDefinition.cif.METRIC_NAME = "Copy of " + src.WB_MetricDefinition.cif.METRIC_NAME;
        tgt.Identity.setAll(tgt.WB_MetricDefinition.cif.WB_MTRDFN_ID, tgt.WB_MetricDefinition.cif.METRIC_ID, tgt.WB_MetricDefinition.cif.METRIC_NAME);

        for (let aRow of src.WB_MetricDefinition.WB_MetricDefinitionGLAccountRows) {
            if (!aRow.isActive)
                continue;
            await tgt.addGLAcct(aRow.cif.WB_GLACCT_ID);
        }

        for (let aRow of src.WB_MetricDefinition.WB_MetricDefinitionEventSpendRows) {
            if (!aRow.isActive)
                continue;
            await tgt.addSpendType(aRow.cif.WB_EVTSTYPE_ID);
        }

        return tgt;
    }

//</editor-fold>

//<editor-fold desc="GL Account operations">
    addGLAcct(aWB_GLAccount_Id: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject) => {
            let aGLAcctRow: WB_GeneralLedgerAccountRow = undefined;

            self.dbContext.find(esWB_General_Ledger_Account, aWB_GLAccount_Id)
                .then((aGLAcct: WB_GeneralLedgerAccountRow) => {
                    if (!aGLAcct) {
                        return;
                    }

                    aGLAcctRow = aGLAcct;
                    return self.dbContext.createRecordWithKey(esWB_MetricDefinition_GLAccount);
                })
                .then((aNewRow: WB_MetricDefinitionGLAccountRow) => {
                    if (!aGLAcctRow) {
                        return;
                    }

                    aNewRow.setDefaults();
                    aNewRow.WB_MetricDefinitionRow = self.drWB_MetricDefinition;
                    aNewRow.cif.WB_MTRDFN_ID = self.drWB_MetricDefinition.cif.WB_MTRDFN_ID;
                    aNewRow.cif.WB_GLACCT_ID = aGLAcctRow.cif.WB_GLACCT_ID;
                    aNewRow.WB_GeneralLedgerAccountRow = aGLAcctRow;
                    self.dbContext.add(esWB_MetricDefinition_GLAccount, aNewRow);
                }).then(() => {
                resolve(self);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    removeGLAcct(aWB_MTRDFNGLR_ID: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject) => {
            _.forEach(self.drWB_MetricDefinition.WB_MetricDefinitionGLAccountRows, (aMtrDfn_GLAcct_ItemRow: WB_MetricDefinitionGLAccountRow) => {
                if (aMtrDfn_GLAcct_ItemRow.isActive && aMtrDfn_GLAcct_ItemRow.cif.WB_MTRDFNGLR_ID == aWB_MTRDFNGLR_ID) {
                    aMtrDfn_GLAcct_ItemRow.markDeleted();
                }
            });
            resolve(self);
        });
    }

//</editor-fold>

    //<editor-fold desc="Spend Type operations">

    addSpendType(aWBSpendTypeId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject) => {
            let aSpendTypeRow: WB_TPM_EventSpendTypeRow = undefined;

            self.dbContext.find(esWB_Event_Spend_Type, aWBSpendTypeId)
                .then((aESType: WB_TPM_EventSpendTypeRow) => {
                    if (!aESType) {
                        return;
                    }

                    aSpendTypeRow = aESType;
                    return self.dbContext.createRecordWithKey(esWB_MetricDefinition_EventSpend_Type);
                })
                .then((aNewRow: WB_MetricDefinitionEventSpendRow) => {
                    if (!aSpendTypeRow) {
                        return;
                    }

                    aNewRow.setDefaults();
                    aNewRow.WB_MetricDefinitionRow = self.drWB_MetricDefinition;
                    aNewRow.cif.WB_MTRDFN_ID = self.drWB_MetricDefinition.cif.WB_MTRDFN_ID;
                    aNewRow.cif.WB_EVTSTYPE_ID = aSpendTypeRow.cif.WB_EVTSTYPE_ID;
                    aNewRow.WB_TPM_EventSpendTypeRow = aSpendTypeRow;
                    self.dbContext.add(esWB_MetricDefinition_EventSpend_Type, aNewRow);
                }).then(() => {
                resolve(self);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    removeSpendType(aWBMtrDfnSpendTypeId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject) => {
            _.forEach(self.drWB_MetricDefinition.WB_MetricDefinitionEventSpendRows, (aRow) => {
                if (aRow.isActive && aRow.cif.WB_MTRDFNEVTSTYPER_ID == aWBMtrDfnSpendTypeId) {
                    aRow.markDeleted();
                }
            });
            resolve(self);
        });
    }

    //</editor-fold>


    //<editor-fold desc = "IRecordResolver operations -- static">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const metric_dfn_options: CreateOptions = {
            standardConditions: {
                [esWB_Metric_Definition]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_MetricDefinition_GLAccount]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_MetricDefinition_EventSpend_Type]: { "DELETED_IND": { "notIn": ["1"] } },
                [esWB_MetricDefinition_Component]: { "DELETED_IND": { "notIn": ["1"] } }
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_CubeSource_DataSource]: [esWB_CubeSource, esWB_DataObject]
            }
        };

        const aRecResolver = new ESRecordResolver(dbContext, metric_dfn_options);

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Metric_Definition, ids).then((result) => {
                resolve();
            }).catch((err) => {
                error(err);
            });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>

    //<editor-fold desc="IHaveSecurity operations">

    private _PolicyCollection: IPolicyCollection;
    private _bIHaveSecurityLoaded: boolean;
    private _OnIHaveSecurityLoaded: Promise<IHaveSecurity>;

    IHaveSecurity_init() {
        this._PolicyCollection = new PolicyCollection();
        this._bIHaveSecurityLoaded = false;
        this._OnIHaveSecurityLoaded = undefined;
    }


    IHaveSecurity_OnLoadComplete(): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            self.OnIHaveSecurityLoaded
                .then((ignore) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    async IHaveSecurity_AfterSaveComplete(): Promise<void> {
        const self = this;

        let policy: MetricDefinitionPolicy = self._PolicyCollection["MetricDefinition"];

        if (policy)
            await policy.refreshPolicy(true);

    }


    get OnIHaveSecurityLoaded(): Promise<IHaveSecurity> {
        const self = this;

        if (self.bIHaveSecurityLoaded) {
            return ASync.resolve(this);
        }

        if (self._OnIHaveSecurityLoaded) {
            return self._OnIHaveSecurityLoaded;
        }

        let aBOis = new PolicyBOis(["Super_User", "Metric_Definition_Editor"], ["Metric_Definition_Viewer"]);
        self.PolicyCollection.addPolicy("BOis", aBOis);

        let aHasRolePolicy: PolicyHasRole;
        let aMetricDefinitionPolicy: MetricDefinitionPolicy;

        this._OnIHaveSecurityLoaded = new Promise<IHaveSecurity>((resolve, error) => {
            aHasRolePolicy = new PolicyHasRole();
            aHasRolePolicy.OnLoadComplete
                .then((ignore) => {
                    self.PolicyCollection.addPolicy("HasRole", aHasRolePolicy);
                    aMetricDefinitionPolicy = new MetricDefinitionPolicy(self);
                    return aMetricDefinitionPolicy.OnLoadComplete;
                })
                .then((ignore) => {
                    self.PolicyCollection.addPolicy("MetricDefinition", aMetricDefinitionPolicy);
                    self._bIHaveSecurityLoaded = true;
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });

        });

        return self._OnIHaveSecurityLoaded;
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection;
    }

    get bIHaveSecurityLoaded(): boolean {
        return this._bIHaveSecurityLoaded;
    }


    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(MetricDefinitionBO, "MetricDefinitionBO", "Metric Definition");